<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const props = defineProps({
  elementType: { type: Number, required: true },
  elementKey: { type: String, default: '' },
  captureData: { type: Function, required: true },
  builtins: { type: Array, default: () => [] },
  initialBuiltin: { type: [String, Number], default: undefined },
  menuTitle: { type: String, default: '' },
  icon: { type: String, default: 'mdi-view-dashboard-outline' },
  supportsDefault: { type: Boolean, default: false },
  supportsShare: { type: Boolean, default: true },
})

const emit = defineEmits(['select-builtin', 'load', 'default-resolved'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { notifySuccess } = useNotify()

const isAdmin = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS))

const layouts = ref([])
const activeId = ref(null)
const activeBuiltin = ref(props.initialBuiltin !== undefined ? props.initialBuiltin : props.builtins[0]?.value ?? null)

const showBuiltinHeader = computed(() => props.builtins.length > 1)

const savedLayouts = computed(() => layouts.value.filter(l => !l.builtin))
const defaultSaved = computed(() => savedLayouts.value.find(l => l.is_default) ?? null)
const defaultMarker = computed(() => layouts.value.find(l => l.builtin && l.is_default) ?? null)
const systemBuiltinValue = computed(() => props.builtins[0]?.value ?? null)
const defaultBuiltinValue = computed(() => defaultSaved.value ? null : (defaultMarker.value ? defaultMarker.value.builtin : systemBuiltinValue.value))
const isBuiltinDefault = (builtin) => !defaultSaved.value && defaultBuiltinValue.value === builtin.value

const activeLabel = computed(() => {
  if (activeId.value !== null) return layouts.value.find(l => l.id === activeId.value)?.name ?? props.menuTitle
  const builtin = props.builtins.find(b => b.value === activeBuiltin.value)
  return builtin?.label ?? props.menuTitle ?? i18n.t('common.layout')
})

const layoutIcon = (layout) => layout.is_shared ? 'mdi-account-group' : 'mdi-account-outline'

const refreshLayouts = async() => {
  layouts.value = await webSiteStore.fetchElementLayoutsAsync(props.elementType, props.elementKey)
}

const replaceLayout = (layout) => {
  const index = layouts.value.findIndex(l => l.id === layout.id)
  layouts.value = index === -1 ? [...layouts.value, layout] : layouts.value.map(l => l.id === layout.id ? layout : l)
}

const onSelectBuiltin = (value) => {
  activeBuiltin.value = value
  activeId.value = null
  emit('select-builtin', value)
}

const onLoadLayout = (layout) => {
  activeId.value = layout.id
  emit('load', layout)
}

const onUpdateLayout = async(layout) => {
  const res = await webSiteStore.updateElementLayoutAsync(layout.id, { data: props.captureData() })
  replaceLayout(res.data)
  notifySuccess(i18n.t('info.updatedSuccessfully'))
}

const onToggleShared = async(layout) => {
  const res = await webSiteStore.toggleElementLayoutSharedAsync(layout.id)
  replaceLayout(res.data)
}

const applyDefaultChange = async(action) => {
  await action()
  await refreshLayouts()
}

const onStarSaved = (layout) => {
  if (!isAdmin.value || layout.is_default) {
    return
  }
  applyDefaultChange(() => webSiteStore.setDefaultElementLayoutAsync(layout.id))
  onLoadLayout(layout)
}

const onStarBuiltin = (builtin) => {
  if (!isAdmin.value || isBuiltinDefault(builtin)) {
    return
  }
  applyDefaultChange(() => builtin.value === systemBuiltinValue.value
    ? webSiteStore.clearDefaultElementLayoutAsync(props.elementType, props.elementKey)
    : webSiteStore.setDefaultBuiltinElementLayoutAsync(props.elementType, props.elementKey, builtin.value))
  onSelectBuiltin(builtin.value)
}

const showSaveDialog = ref(false)
const newLayoutName = ref('')
const newLayoutShared = ref(false)

const onSaveAsNew = () => {
  newLayoutName.value = ''
  newLayoutShared.value = false
  showSaveDialog.value = true
}

const onConfirmSaveNew = async() => {
  const name = newLayoutName.value.trim()
  if (!name) {
    return
  }
  const res = await webSiteStore.upsertElementLayoutAsync({
    element_type: props.elementType,
    element_key: props.elementKey,
    name,
    data: props.captureData(),
    is_shared: newLayoutShared.value,
  })
  replaceLayout(res.data)
  activeId.value = res.data.id
  showSaveDialog.value = false
  notifySuccess(i18n.t('info.updatedSuccessfully'))
}

const layoutToDelete = ref(null)
const showDeleteDialog = computed({
  get: () => layoutToDelete.value !== null,
  set: (val) => { if (!val) layoutToDelete.value = null },
})

const onDelete = (layout) => { layoutToDelete.value = layout }

const onDeleteConfirmed = async() => {
  const deletedId = layoutToDelete.value.id
  await webSiteStore.deleteElementLayoutAsync(deletedId)
  layouts.value = layouts.value.filter(l => l.id !== deletedId)
  if (activeId.value === deletedId) {
    onSelectBuiltin(props.builtins[0]?.value ?? null)
  }
  notifySuccess(i18n.t('info.deletedSuccessfully'))
}

onMounted(async() => {
  await refreshLayouts()
  if (defaultSaved.value) {
    activeId.value = defaultSaved.value.id
  } else if (defaultBuiltinValue.value !== null) {
    activeBuiltin.value = defaultBuiltinValue.value
  }
  emit('default-resolved', { builtin: defaultBuiltinValue.value, layout: defaultSaved.value })
})
</script>
<template>
  <q-btn-dropdown
    flat
    no-caps
    :icon="icon"
    :label="activeLabel"
    color="grey-7"
    class="q-px-xs"
  >
    <q-list style="min-width: 18rem">
      <q-item
        v-close-popup
        clickable
        class="q-pl-lg"
        @click="onSaveAsNew"
      >
        <q-item-section avatar><q-icon name="mdi-content-save-plus-outline" size="xs" /></q-item-section>
        <q-item-section>{{ i18n.t('common.saveLayoutAs') }}</q-item-section>
      </q-item>

      <q-separator />
      <q-item-label v-if="showBuiltinHeader" header class="text-caption">{{ i18n.t('common.defaultLayouts') }}</q-item-label>
      <q-item
        v-for="builtin in builtins"
        :key="`builtin-${builtin.value}`"
        v-close-popup
        clickable
        :active="activeId === null && activeBuiltin === builtin.value"
        active-class="text-primary"
        class="q-pl-lg"
        @click="onSelectBuiltin(builtin.value)"
      >
        <q-item-section avatar><q-icon :name="builtin.icon" size="xs" /></q-item-section>
        <q-item-section>{{ builtin.label }}</q-item-section>
        <q-item-section v-if="supportsDefault && (isBuiltinDefault(builtin) || isAdmin)" side>
          <q-btn
            flat
            round
            size="xs"
            :icon="isBuiltinDefault(builtin) ? 'mdi-star' : 'mdi-star-outline'"
            :color="isBuiltinDefault(builtin) ? 'amber' : undefined"
            :class="{ 'layout-star--hover': !isBuiltinDefault(builtin) }"
            @click.stop="onStarBuiltin(builtin)"
          >
            <q-tooltip v-if="!isBuiltinDefault(builtin)">{{ i18n.t('common.setAsDefault') }}</q-tooltip>
          </q-btn>
        </q-item-section>
      </q-item>

      <template v-if="savedLayouts.length">
        <q-separator />
        <q-item-label header class="text-caption q-pb-xs">{{ i18n.t('common.savedLayouts') }}</q-item-label>
        <q-item
          v-for="layout in savedLayouts"
          :key="layout.id"
          clickable
          :active="layout.id === activeId"
          active-class="text-primary"
          class="q-pl-lg"
        >
          <q-item-section avatar @click="onLoadLayout(layout)">
            <q-icon :name="layoutIcon(layout)" size="xs" />
          </q-item-section>
          <q-item-section @click="onLoadLayout(layout)">
            {{ layout.name }}
            <q-tooltip>{{ layout.created_by_name }}</q-tooltip>
          </q-item-section>
          <q-item-section
            v-if="(supportsDefault && (layout.is_default || isAdmin)) || webSiteStore.canManageOwned(layout, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)"
            side
          >
            <div class="row items-center no-wrap">
              <q-btn
                v-if="supportsDefault && (layout.is_default || isAdmin)"
                flat
                round
                size="xs"
                :icon="layout.is_default ? 'mdi-star' : 'mdi-star-outline'"
                :color="layout.is_default ? 'amber' : undefined"
                :class="{ 'layout-star--hover': !layout.is_default }"
                @click.stop="onStarSaved(layout)"
              >
                <q-tooltip v-if="!layout.is_default">{{ i18n.t('common.setAsDefault') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="webSiteStore.canManageOwned(layout, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)"
                flat
                round
                size="xs"
                icon="mdi-dots-vertical"
                @click.stop
              >
                <q-menu auto-close style="min-width: 15rem">
                  <q-list dense>
                    <q-item v-close-popup clickable @click="onUpdateLayout(layout)">
                      <q-item-section avatar><q-icon name="mdi-content-save-outline" size="xs" /></q-item-section>
                      <q-item-section>{{ i18n.t('common.update') }}</q-item-section>
                    </q-item>
                    <q-item
                      v-if="supportsShare && isAdmin"
                      v-close-popup
                      clickable
                      @click="onToggleShared(layout)"
                    >
                      <q-item-section avatar><q-icon name="mdi-account-group" size="xs" /></q-item-section>
                      <q-item-section>{{ layout.is_shared ? i18n.t('common.stopSharing') : i18n.t('common.shareWithEveryone') }}</q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      class="text-negative"
                      @click="onDelete(layout)"
                    >
                      <q-item-section avatar><q-icon name="mdi-delete" size="xs" /></q-item-section>
                      <q-item-section>{{ i18n.t('common.delete') }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </q-item-section>
        </q-item>
      </template>
    </q-list>
  </q-btn-dropdown>

  <q-dialog v-model="showSaveDialog" class="dialog-xs">
    <q-card>
      <q-card-section class="text-subtitle1">{{ i18n.t('common.saveLayoutAs') }}</q-card-section>
      <q-card-section class="q-pt-none q-gutter-sm">
        <q-input
          v-model="newLayoutName"
          :label="i18n.t('common.layoutName')"
          outlined
          autofocus
          @keyup.enter="onConfirmSaveNew"
        />
        <q-toggle v-if="supportsShare && isAdmin" v-model="newLayoutShared" :label="i18n.t('common.shareWithEveryone')" />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn v-close-popup flat :label="i18n.t('common.cancel')" />
        <q-btn
          color="primary"
          unelevated
          :label="i18n.t('common.save')"
          :disable="!newLayoutName.trim()"
          @click="onConfirmSaveNew"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <confirm-dialog
    v-model="showDeleteDialog"
    :message="i18n.t('common.confirmDeleteMessage', { item: layoutToDelete?.name })"
    icon="mdi-delete-outline"
    icon-color="negative"
    @confirm="onDeleteConfirmed"
  />
</template>
<style lang="scss" scoped>
.layout-star--hover {
  opacity: 0;
  transition: opacity 0.15s;
}
.q-item:hover .layout-star--hover {
  opacity: 1;
}
</style>
